import {
    SALE_ADD_CUSTOMER,
    SALE_ADD_PRODUCT,
    SALE_CHANGE_QUANTITY,
    SALE_CLEAR,
    SALE_REMOVE_CUSTOMER,
    SALE_REMOVE_PRODUCT,
    SALE_RETRIEVE,
    SALE_UPDATE_PRODUCTS,
} from "../../../Actions/SellScreen/SellScreenConstants";
import { type ApplicationAgent } from "./ApplicationAgent";

export enum ApplicationEventsFromShopfront {
    READY = "READY",
    REQUEST_SETTINGS = "REQUEST_SETTINGS",
    REQUEST_BUTTONS = "REQUEST_BUTTONS",
    REQUEST_TABLE_COLUMNS = "REQUEST_TABLE_COLUMNS",
    REQUEST_SELL_SCREEN_OPTIONS = "REQUEST_SELL_SCREEN_OPTIONS",
    CALLBACK = "CALLBACK",
    CYCLE_KEY = "CYCLE_KEY",
    LOCATION_CHANGED = "LOCATION_CHANGED",
    RESPONSE_CURRENT_SALE = "RESPONSE_CURRENT_SALE",
    INTERNAL_PAGE_MESSAGE = "INTERNAL_PAGE_MESSAGE",
    REGISTER_CHANGED = "REGISTER_CHANGED",
    RESPONSE_DATABASE_REQUEST = "RESPONSE_DATABASE_REQUEST",
    RESPONSE_LOCATION = "RESPONSE_LOCATION",
    FORMAT_INTEGRATED_PRODUCT = "FORMAT_INTEGRATED_PRODUCT",
    REQUEST_CUSTOMER_LIST_OPTIONS = "REQUEST_CUSTOMER_LIST_OPTIONS",
    SALE_COMPLETE = "SALE_COMPLETE",
    REQUEST_SALE_KEYS = "REQUEST_SALE_KEYS",
    RESPONSE_OPTION = "RESPONSE_OPTION",
    UI_PIPELINE = "UI_PIPELINE",
    PAYMENT_METHODS_ENABLED = "PAYMENT_METHODS_ENABLED",
    RESPONSE_AUDIO_REQUEST = "RESPONSE_AUDIO_REQUEST",
    AUDIO_PERMISSION_CHANGE = "AUDIO_PERMISSION_CHANGE",
    AUDIO_READY = "AUDIO_READY",
    RESPONSE_SECURE_KEY = "RESPONSE_SECURE_KEY",
    FULFILMENT_GET_ORDER = "FULFILMENT_GET_ORDER",
    FULFILMENT_VOID_ORDER = "FULFILMENT_VOID_ORDER",
    FULFILMENT_PROCESS_ORDER = "FULFILMENT_PROCESS_ORDER",
    FULFILMENT_ORDER_APPROVAL = "FULFILMENT_ORDER_APPROVAL",
    FULFILMENT_ORDER_COLLECTED = "FULFILMENT_ORDER_COLLECTED",
    FULFILMENT_ORDER_COMPLETED = "FULFILMENT_ORDER_COMPLETED",
    RESPONSE_CREATE_SALE = "RESPONSE_CREATE_SALE",
    GIFT_CARD_CODE_CHECK = "GIFT_CARD_CODE_CHECK",
    SALE_PRE_FINISH_PIPELINE = "SALE_PRE_FINISH_PIPELINE",
}

export const ApplicationEventsToShopfront = {
    READY                          : "READY",
    SERIALIZED                     : "SERIALIZED",
    RESPONSE_BUTTONS               : "RESPONSE_BUTTONS",
    RESPONSE_SETTINGS              : "RESPONSE_SETTINGS",
    RESPONSE_SELL_SCREEN_OPTIONS   : "RESPONSE_SELL_SCREEN_OPTIONS",
    DOWNLOAD                       : "DOWNLOAD",
    LOAD                           : "LOAD",
    REQUEST_CURRENT_SALE           : "REQUEST_CURRENT_SALE",
    DATABASE_REQUEST               : "DATABASE_REQUEST",
    UNSUPPORTED_EVENT              : "UNSUPPORTED_EVENT",
    NOT_LISTENING_TO_EVENT         : "NOT_LISTENING_TO_EVENT",
    REQUEST_LOCATION               : "REQUEST_LOCATION",
    RESPONSE_SALE_KEYS             : "RESPONSE_SALE_KEYS",
    PRINT_RECEIPT                  : "PRINT_RECEIPT",
    REDIRECT                       : "REDIRECT",
    GET_OPTION                     : "GET_OPTION",
    AUDIO_REQUEST_PERMISSION       : "AUDIO_REQUEST_PERMISSION",
    AUDIO_PRELOAD                  : "AUDIO_PRELOAD",
    AUDIO_PLAY                     : "AUDIO_PLAY",
    FULFILMENT_GET_ORDER           : "FULFILMENT_GET_ORDER",
    FULFILMENT_OPT_IN              : "FULFILMENT_OPT_IN",
    FULFILMENT_ORDERS_SYNC         : "FULFILMENT_ORDERS_SYNC",
    FULFILMENT_ORDERS_CREATE       : "FULFILMENT_ORDERS_CREATE",
    FULFILMENT_ORDERS_UPDATE       : "FULFILMENT_ORDERS_UPDATE",
    FULFILMENT_ORDERS_CANCEL       : "FULFILMENT_ORDERS_CANCEL",
    CREATE_SALE                    : "CREATE_SALE",
    CHANGE_SELL_SCREEN_ACTION_MODE : "CHANGE_SELL_SCREEN_ACTION_MODE",
    CHANGE_SELL_SCREEN_SUMMARY_MODE: "CHANGE_SELL_SCREEN_SUMMARY_MODE",
    REQUEST_SECURE_KEY             : "REQUEST_SECURE_KEY",
    ROTATE_SIGNING_KEY             : "ROTATE_SIGNING_KEY",
    LISTENERS                      : {
        SELL_SCREEN_OPTION_CHANGE       : "SELL_SCREEN_OPTION_CHANGE",
        INTERNAL_PAGE_MESSAGE           : "INTERNAL_PAGE_MESSAGE",
        TABLE_UPDATE                    : "TABLE_UPDATE",
        PIPELINE_TRIGGER                : "PIPELINE_TRIGGER",
        SELL_SCREEN_PROMOTION_APPLICABLE: "SELL_SCREEN_PROMOTION_APPLICABLE",
    },
    RESPONSE_GIFT_CARD_CODE_CHECK: "RESPONSE_GIFT_CARD_CODE_CHECK",
};

export const ApplicationInternalEvents = {
    APPLICATION_READY: "EMBEDDED_APPLICATION_READY",
} as const;

export interface ApplicationListenerSellScreenOptionChange {
    id: string;
    url: string;
    title: string;
}

export interface ApplicationInternalMessageEvent {
    application: ApplicationAgent;
    data: {
        url: string;
        method: string;
        message: any;
    };
}

export interface ApplicationUIPipelineContent {
    name: string;
    content: string;
}

/**
 * A list of Redux events to send to the Embedded API. Add items to this list which should wait for promotion
 * calculation to complete before sending to the application.
 *
 * If you don't need to wait for promotion calculation to complete, add the event to the
 * `supportedApplicationEventsFromRedux` list
 */
export const promotionTrackingApplicationEventsFromRedux = [
    SALE_ADD_PRODUCT,
    SALE_REMOVE_PRODUCT,
    SALE_ADD_CUSTOMER,
    SALE_REMOVE_CUSTOMER,
    SALE_UPDATE_PRODUCTS,
    SALE_CHANGE_QUANTITY,
    SALE_RETRIEVE,
];

/**
 * A list of Redux events to send to the Embedded API. These items send directly to the application as soon as they're
 * resolved.
 *
 * If you need to wait for promotion calculation to complete, add the event to the
 * `promotionTrackingApplicationEventsFromRedux` list
 */
export const supportedApplicationEventsFromRedux = [
    SALE_CLEAR,
] as const;

export type ApplicationEventsFromRedux = typeof promotionTrackingApplicationEventsFromRedux[number] |
    typeof supportedApplicationEventsFromRedux[number];
